import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPhone, FaEnvelope, FaEdit, FaTrash, FaPlus, FaTimes, FaChevronDown, FaChevronRight, FaMailBulk } from "react-icons/fa";
import { Base_Url } from '../config';
import './QuickContacts.css';
import { selectUserExists } from '../redux/userSlice';
import { useSelector } from 'react-redux';

const QuickContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [expandedContacts, setExpandedContacts] = useState({});
  const [formData, setFormData] = useState({
    parent_text: '',
    parent_email: '',
    sub_emails: [{ text: '', email: '' }]
  });

  const userExists = useSelector(selectUserExists);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Base_Url}/parents`);
      setContacts(response.data);

      // Initially all contacts are collapsed (false)
      const initialExpanded = {};
      response.data.forEach(contact => {
        initialExpanded[contact.id] = false;
      });
      setExpandedContacts(initialExpanded);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const toggleContact = (contactId) => {
    setExpandedContacts(prev => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubEmailChange = (index, field, value) => {
    const newSubEmails = [...formData.sub_emails];
    newSubEmails[index] = {
      ...newSubEmails[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      sub_emails: newSubEmails
    }));
  };

  const addSubEmailField = () => {
    setFormData(prev => ({
      ...prev,
      sub_emails: [...prev.sub_emails, { text: '', email: '' }]
    }));
  };

  const removeSubEmailField = (index) => {
    if (formData.sub_emails.length > 1) {
      const newSubEmails = formData.sub_emails.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        sub_emails: newSubEmails
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      parent_text: '',
      parent_email: '',
      sub_emails: [{ text: '', email: '' }]
    });
    setEditingContact(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (contact, e) => {
    e.stopPropagation();
    setFormData({
      parent_text: contact.display_name || '',
      parent_email: contact.parent_email,
      sub_emails: contact.sub_emails.length > 0
        ? contact.sub_emails.map(sub => ({
          text: sub.title || '',
          email: sub.sub_email
        }))
        : [{ text: '', email: '' }]
    });
    setEditingContact(contact);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingContact) {
        await axios.put(`${Base_Url}/parent/${editingContact.id}`, {
          parent_email: formData.parent_email,
          display_name: formData.parent_text
        });

        for (const subEmail of formData.sub_emails) {
          if (subEmail.email.trim()) {
            const existingSub = editingContact.sub_emails.find(sub => sub.sub_email === subEmail.email);
            if (existingSub) {
              await axios.put(`${Base_Url}/sub/${existingSub.id}`, {
                sub_email: subEmail.email,
                title: subEmail.text
              });
            } else {
              await axios.post(`${Base_Url}/sub`, {
                parent_id: editingContact.id,
                sub_email: subEmail.email.trim(),
                title: subEmail.text
              });
            }
          }
        }
      } else {
        const parentResponse = await axios.post(`${Base_Url}/parent`, {
          parent_email: formData.parent_email,
          display_name: formData.parent_text
        });

        for (const subEmail of formData.sub_emails) {
          if (subEmail.email.trim()) {
            await axios.post(`${Base_Url}/sub`, {
              parent_id: parentResponse.data.parent_id,
              sub_email: subEmail.email.trim(),
              title: subEmail.text
            });
          }
        }
      }

      setShowModal(false);
      resetForm();
      fetchContacts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contact');
    }
  };

  const handleDelete = async (contactId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this contact and all its emails?')) {
      try {
        await axios.delete(`${Base_Url}/parent/${contactId}`);
        fetchContacts();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete contact');
      }
    }
  };

  const handleDeleteSubEmail = async (subEmailId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this email?')) {
      try {
        await axios.delete(`${Base_Url}/sub/${subEmailId}`);
        fetchContacts();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete email');
      }
    }
  };

  if (loading) {
    return (
      <div className="quick-contacts-container">
        <div className="quick-contacts-card">
          <div className="quick-contacts-loading-container">
            <div className="quick-contacts-spinner"></div>
            <div>Loading contacts...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quick-contacts-container">
        <div className="quick-contacts-card">
          <div className="quick-contacts-error-container">
            <div className="quick-contacts-error-box">
              <p className="quick-contacts-error-text">Error: {error}</p>
              <button
                className="quick-contacts-dismiss-button"
                onClick={() => setError(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="quick-contacts-container">
        <div className="quick-contacts-card">
          <div className="quick-contacts-header">
            <h4 className="quick-contacts-header-title">
              <div className="quick-contacts-icon-wrapper">
                <FaMailBulk />
              </div>
              Quick Contacts
            </h4>
            {/* Add button only shows if user exists */}
            {userExists && (
              <button
                className="quick-contacts-add-button"
                onClick={openAddModal}
              >
                <div className="quick-contacts-icon-wrapper">
                  <FaPlus />
                </div>
              </button>
            )}
          </div>

          <hr className="quick-contacts-divider m-0" />


          <div className="quick-contacts-body">
            {contacts.length === 0 ? (
              <div className="quick-contacts-empty-state">
                <p className="quick-contacts-empty-text">No contacts found</p>
                {/* Add First Contact button only shows if user exists */}
                {userExists && (
                  <button
                    className="quick-contacts-primary-button"
                    onClick={openAddModal}
                  >
                    <div className="quick-contacts-icon-wrapper">
                      <FaPlus />
                    </div>
                    Add First Contact
                  </button>
                )}
              </div>
            ) : (
              contacts.map((contact) => (
                <div key={contact.id} className="quick-contacts-contact-parent">
                  <div
                    className="quick-contacts-contact-header"
                    style={{
                      backgroundColor: expandedContacts[contact.id] ? '#f8f9fa' : 'white'
                    }}
                    onClick={() => toggleContact(contact.id)}
                  >
                    <div className="quick-contacts-contact-header-left">
                      <div className="quick-contacts-expand-icon">
                        <div className="quick-contacts-icon-wrapper">
                          {expandedContacts[contact.id] ? <FaChevronDown /> : <FaChevronRight />}
                        </div>
                      </div>
                      <div className="quick-contacts-contact-icon">
                        <div className="quick-contacts-icon-wrapper">
                          <FaEnvelope />
                        </div>
                      </div>
                      <div>
                        <h6 className="quick-contacts-contact-title">
                          {contact.parent_email}
                        </h6>
                        <small className="quick-contacts-contact-subtitle">
                          {contact.display_name || 'Main Email'}
                        </small>
                      </div>
                    </div>
                    {/* Edit and Delete buttons only show if user exists */}
                    {userExists && (
                      <div className="quick-contacts-contact-actions">
                        <button
                          className="quick-contacts-edit-button"
                          onClick={(e) => openEditModal(contact, e)}
                          title="Edit Contact"
                        >
                          <div className="quick-contacts-icon-wrapper">
                            <FaEdit />
                          </div>
                        </button>
                        <button
                          className="quick-contacts-delete-button"
                          onClick={(e) => handleDelete(contact.id, e)}
                          title="Delete Contact"
                        >
                          <div className="quick-contacts-icon-wrapper">
                            <FaTrash />
                          </div>
                        </button>
                      </div>
                    )}
                  </div>

                  {expandedContacts[contact.id] && (
                    <div className="quick-contacts-contact-children">
                      {contact.sub_emails.map((sub) => (
                        <div key={sub.id} className="quick-contacts-sub-email-item">
                          <div className="quick-contacts-email-item-content">
                            <div>
                              <small className="quick-contacts-sub-email-label">{sub.title || 'Sub Email'}:</small>
                              <span className="quick-contacts-email-value">{sub.sub_email}</span>
                            </div>
                            {/* Delete sub-email button only shows if user exists */}
                            {userExists && (
                              <button
                                className="quick-contacts-delete-sub-button"
                                onClick={(e) => handleDeleteSubEmail(sub.id, e)}
                                title="Delete Email"
                              >
                                <div className="quick-contacts-icon-wrapper">
                                  <FaTimes />
                                </div>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {contact.sub_emails.length === 0 && (
                        <div className="quick-contacts-empty-sub-emails">
                          <small className="quick-contacts-empty-sub-text">No emails</small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

        </div>
      </div>

      {/* Modal - Only accessible if user exists */}
      {userExists && showModal && (
        <div className="quick-contacts-modal-overlay">
          <div className="quick-contacts-modal">
            <div className="quick-contacts-modal-header">
              <h5 className="quick-contacts-modal-title">
                {editingContact ? 'Edit Contact' : 'Add New Contact'}
              </h5>
              <button
                className="quick-contacts-close-button"
                onClick={() => setShowModal(false)}
              >
                <div className="quick-contacts-icon-wrapper">
                  <FaTimes />
                </div>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="quick-contacts-modal-body">
                <div className="quick-contacts-section">
                  <h6 className="quick-contacts-section-title">Main Email</h6>
                  <div className="quick-contacts-form-row">
                    <div className="quick-contacts-form-group">
                      <label className="quick-contacts-label">Display Text *</label>
                      <input
                        type="text"
                        className="quick-contacts-input"
                        name="parent_text"
                        value={formData.parent_text}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Main Email, Help Desk"
                      />
                    </div>
                    <div className="quick-contacts-form-group">
                      <label className="quick-contacts-label">Email Address *</label>
                      <input
                        type="email"
                        className="quick-contacts-input"
                        name="parent_email"
                        value={formData.parent_email}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., help@cc.iitd.ac.in"
                      />
                    </div>
                  </div>
                </div>

                <div className="quick-contacts-section">
                  <h6 className="quick-contacts-section-title">Child Emails</h6>
                  {formData.sub_emails.map((sub, index) => (
                    <div key={index} className="quick-contacts-sub-email-box">
                      <div className="quick-contacts-form-row">
                        <div className="quick-contacts-form-group" style={{ flex: 1 }}>
                          <label className="quick-contacts-small-label">Display Text</label>
                          <input
                            type="text"
                            className="quick-contacts-input"
                            value={sub.text}
                            onChange={(e) => handleSubEmailChange(index, 'text', e.target.value)}
                            placeholder="e.g., HPC, Networking"
                          />
                        </div>
                        <div className="quick-contacts-form-group" style={{ flex: 1 }}>
                          <label className="quick-contacts-small-label">Email Address</label>
                          <input
                            type="email"
                            className="quick-contacts-input"
                            value={sub.email}
                            onChange={(e) => handleSubEmailChange(index, 'email', e.target.value)}
                            placeholder="e.g., hpchelp@iitd.ac.in"
                          />
                        </div>
                        {formData.sub_emails.length > 1 && (
                          <div className="quick-contacts-remove-button-container">
                            <button
                              type="button"
                              className="quick-contacts-remove-button"
                              onClick={() => removeSubEmailField(index)}
                            >
                              <div className="quick-contacts-icon-wrapper">
                                <FaTimes />
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="quick-contacts-add-sub-button"
                    onClick={addSubEmailField}
                  >
                    <div className="quick-contacts-icon-wrapper">
                      <FaPlus />
                    </div>
                    Add Child Email
                  </button>
                </div>
              </div>
              <div className="quick-contacts-modal-footer">
                <button
                  type="button"
                  className="quick-contacts-cancel-button"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="quick-contacts-submit-button">
                  {editingContact ? 'Update' : 'Create'} Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickContacts;